import { useEffect, useMemo, useState } from "react";
import { differenceInCalendarDays, format } from "date-fns";
import { CalendarClock, GraduationCap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { useHasPermission, useAuthStore } from "@/store/authStore";
import { getUserDisplayName, useOrgMembers } from "@/store/teamsStore";
import { ONBOARDING_TASK_TYPE, Permission } from "@forgepoint/shared";
import { useOnboardingStore } from "./onboardingStore";
import { AnnouncementsCard } from "./AnnouncementsCard";
import { OnboardingTaskSheet } from "./OnboardingTaskSheet";
import { TaskChecklist } from "./TaskChecklist";
import { OnboardingScheduleCard } from "./OnboardingScheduleCard";
import { parseDateOnly } from "./onboardingDates";

/**
 * The onboarding tab body: the go-live schedule, announcements, the signed-in
 * user's tasks, the UAT checklist, and all tasks. Read-only apart from task
 * check-off.
 */
export function OnboardingController() {
  const currentUserId = useAuthStore((state) => state.user?.id);
  const canInteract = useHasPermission(Permission.ONBOARDING_INTERACT);
  const usersById = useOrgMembers();
  const userNames = useMemo(
    () =>
      new Map(
        [...usersById].map(([id, user]) => [id, getUserDisplayName(user)]),
      ),
    [usersById],
  );

  const plan = useOnboardingStore((state) => state.plan);
  const phaseMap = useOnboardingStore((state) => state.phaseMap);
  const taskMap = useOnboardingStore((state) => state.taskMap);
  const announcementMap = useOnboardingStore((state) => state.announcementMap);
  const isLoadingBoard = useOnboardingStore((state) => state.isLoadingBoard);
  const boardLoaded = useOnboardingStore((state) => state.boardLoaded);
  const boardError = useOnboardingStore((state) => state.boardError);
  const fetchBoard = useOnboardingStore((state) => state.fetchBoard);

  const [openTaskId, setOpenTaskId] = useState<string | null>(null);

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);

  const tasks = useMemo(
    () => [...taskMap.values()].sort((a, b) => a.order - b.order),
    [taskMap],
  );
  const phases = useMemo(
    () => [...phaseMap.values()].sort((a, b) => a.order - b.order),
    [phaseMap],
  );
  const announcements = useMemo(
    () =>
      [...announcementMap.values()].sort(
        (a, b) =>
          Number(b.pinned) - Number(a.pinned) ||
          b.createdAt.getTime() - a.createdAt.getTime(),
      ),
    [announcementMap],
  );

  const myOpenTasks = tasks.filter(
    (task) => !task.completed && task.assigneeUserId === currentUserId,
  );
  const uatTasks = tasks.filter(
    (task) => task.type === ONBOARDING_TASK_TYPE.UAT,
  );
  const regularTasks = tasks.filter(
    (task) => task.type === ONBOARDING_TASK_TYPE.TASK,
  );
  const completedCount = tasks.filter((task) => task.completed).length;

  if (isLoadingBoard && !boardLoaded) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (boardError) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          {boardError}
        </CardContent>
      </Card>
    );
  }

  if (!plan) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          No active onboarding for your organization.
        </CardContent>
      </Card>
    );
  }

  const goLive = plan.goLiveDate ? parseDateOnly(plan.goLiveDate) : null;
  const daysToGoLive = goLive
    ? differenceInCalendarDays(goLive, new Date())
    : null;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <GraduationCap className="h-5 w-5 text-primary" />
          Onboarding
        </h1>
        {goLive && (
          <Badge variant="outline" className="gap-1.5 py-1">
            <CalendarClock className="h-3.5 w-3.5" />
            Go-live {format(goLive, "EEE, MMM d")}
            {daysToGoLive !== null && daysToGoLive >= 0
              ? ` · ${daysToGoLive} day${daysToGoLive === 1 ? "" : "s"}`
              : ""}
          </Badge>
        )}
      </div>

      {tasks.length > 0 && (
        <div className="space-y-1">
          <Progress value={(completedCount / tasks.length) * 100} />
          <p className="text-xs text-muted-foreground">
            {completedCount} of {tasks.length} tasks complete
          </p>
        </div>
      )}

      <OnboardingScheduleCard
        phases={phases}
        scheduleStart={plan.scheduleStart}
        scheduleEnd={plan.scheduleEnd}
        goLiveDate={plan.goLiveDate}
      />

      <AnnouncementsCard announcements={announcements} usersById={usersById} />

      <TaskChecklist
        title="Your tasks"
        tasks={myOpenTasks}
        canInteract={canInteract}
        userNames={userNames}
        onOpenTask={setOpenTaskId}
      />

      <TaskChecklist
        title="UAT — complete before go-live"
        tasks={uatTasks}
        canInteract={canInteract}
        userNames={userNames}
        showProgress
        onOpenTask={setOpenTaskId}
      />

      <TaskChecklist
        title="All tasks"
        tasks={regularTasks}
        canInteract={canInteract}
        userNames={userNames}
        groupBySection
        onOpenTask={setOpenTaskId}
      />

      <OnboardingTaskSheet
        taskId={openTaskId}
        onClose={() => setOpenTaskId(null)}
      />
    </div>
  );
}
